'use client';
import { ReportMetric } from '@mnd-frontend/graphql-schema';
import { Icons } from '@mnd-frontend/ui/publish/components';
import { localizeValue } from '@mnd-frontend/utils';
import React from 'react';
import styled from 'styled-components';

import { useReportStaticData, useReportTranslation } from '../../hooks';
import { MetricCard } from './MetricCard';

const diffDelta = (current: number, previous: number) => {
  return current - previous;
};
const percentDelta = (current: number, previous: number) => {
  if (previous === 0) return '-';
  return `${Math.round(((current - previous) / previous) * 100)}%`;
};
const deltaDirection = (current: number, previous: number) => {
  if (current === previous || previous === 0) return 'neutral';
  return diffDelta(current, previous) > 0 ? 'positive' : 'negative';
};
const numberFormatter = (value: number) => localizeValue(value, { abbrev: true }).toString();

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(19rem, 100%), 1fr));
  gap: 1rem;
`;

export const availableMetric = [
  ReportMetric.Mentions,
  ReportMetric.EstimatedTotalReach,
  ReportMetric.NetSentimentScore,
  ReportMetric.ShareOfVoice,
  ReportMetric.PrValue,
] as const;

export const BrandReportMetrics = ({
  metrics,
  mentions,
  mentionsPreviousYear,
  estimatedReach,
  estimatedReachPreviousYear,
  netSentimentScore,
  shareOfVoice,
  shareOfVoicePreviousYear,
  prValue,
  prValuePreviousYear,
  prValueCurrency,
  trialMentionsDisclaimer,
}: {
  metrics: ReportMetric[];
  mentions: number;
  mentionsPreviousYear: number;
  estimatedReach: number;
  estimatedReachPreviousYear: number;
  netSentimentScore: number;
  shareOfVoice: number;
  shareOfVoicePreviousYear: number;
  prValue: number;
  prValuePreviousYear: number;
  prValueCurrency: string;
  trialMentionsDisclaimer?: string | null;
}) => {
  const { competitorSearchProfiles } = useReportStaticData();
  const { t } = useReportTranslation();
  const visibleMetrics = availableMetric
    .filter(x => metrics.includes(x))
    .filter(x => x !== ReportMetric.ShareOfVoice || competitorSearchProfiles.length > 0);

  const metricElements = {
    [ReportMetric.Mentions]: (
      <MetricCard
        title={t('reports_key_metrics_mentions')}
        value={mentions}
        valueFormatter={numberFormatter}
        yearlyTrendDelta={percentDelta(mentions, mentionsPreviousYear)}
        yearlyTrendDeltaDirection={deltaDirection(mentions, mentionsPreviousYear)}
        icon={<Icons.Story />}
        disclaimer={trialMentionsDisclaimer || undefined}
      />
    ),
    [ReportMetric.EstimatedTotalReach]: (
      <MetricCard
        title={t('reports_key_metrics_reach')}
        value={estimatedReach}
        valueFormatter={numberFormatter}
        yearlyTrendDelta={percentDelta(estimatedReach, estimatedReachPreviousYear)}
        yearlyTrendDeltaDirection={deltaDirection(estimatedReach, estimatedReachPreviousYear)}
        icon={<Icons.Reach />}
      />
    ),
    [ReportMetric.PrValue]: (
      <MetricCard
        title={t('reports_ad-value', { currency: prValueCurrency })}
        value={prValue}
        unit={prValueCurrency}
        valueFormatter={numberFormatter}
        yearlyTrendDelta={percentDelta(prValue, prValuePreviousYear)}
        yearlyTrendDeltaDirection={deltaDirection(prValue, prValuePreviousYear)}
        icon={<Icons.Diamond />}
      />
    ),
    [ReportMetric.ShareOfVoice]: (
      <MetricCard
        title={t('reports_share_of_voice')}
        value={shareOfVoice}
        valueFormatter={(value: number) => localizeValue(value, { abbrev: true }).toString() + '%'}
        yearlyTrendDelta={percentDelta(shareOfVoice, shareOfVoicePreviousYear)}
        yearlyTrendDeltaDirection={deltaDirection(shareOfVoice, shareOfVoicePreviousYear)}
        icon={<Icons.ShareOfVoice />}
      />
    ),
    [ReportMetric.NetSentimentScore]: (
      <MetricCard
        title={t('reports_net_sentiment_score_metric')}
        value={netSentimentScore}
        valueFormatter={numberFormatter}
        unit="/ 100"
        yearlyTrendDeltaDirection="neutral"
        icon={<Icons.SentimentPositive />}
      />
    ),
  };

  return (
    <Grid>
      {visibleMetrics.map(m => (
        <React.Fragment key={m}>{metricElements[m]}</React.Fragment>
      ))}
    </Grid>
  );
};
